import React, {useRef, useEffect, useState} from 'react';
import classNames from 'classnames';
import {
  useAdditionalSeedData,
  useContentElementLifecycle,
  useLocale,
  utils
} from 'pageflow-scrolled/frontend';

import checkmark from '../../images/icons/checkmark.svg';
import chevronLeft from '../../images/icons/chevronLeft.svg';
import chevronRight from '../../images/icons/chevronRight.svg';
import styles from './PricingTable.module.css';

import {translations} from './translations';

const lineLevelProperties = [
  'pageflowNext',
  'pageflowClassic',

  'publishedEntries',
  'users',
  'sites',
  'additionalSites',

  'hasTicketSupport',
  'hasExtendedSupport',

  'hasStylingOptions',
  'hasCustomFonts',
  'hasWhiteLabel',
  'hasIframeEmbedContentElement',
  'hasCustomTheme',

  'hasCustomDomain',
  'hasGoogleAnalytics',
  'hasPlausibleAnalytics',
  'hasCustomAnalytics',
  'hasCustomConsent',
  'hasCustomCertificate',
  'hasSso',

  'cdnTraffic',
  'mediaEncoding'
];

const linesWithSpaceBefore = [
  'publishedEntries',
  'hasTicketSupport',
  'hasStylingOptions',
  'hasCustomDomain',
  'cdnTraffic'
];

export function PricingTable({configuration}) {
  const t = translations[useLocale()];

  const {planTableaux, selectPlans} = useAdditionalSeedData('pricing');

  const scrollerRef = useRef();
  const scrolledRef = useRef(false);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const lines = lineLevelProperties.map((levelProperty, index) => ({
    id: index,
    levelProperty,
    title: t[levelProperty].title,
    description: t[levelProperty].description,
  }));

  function scrollLeft(event) {
    scrollerRef.current.scrollBy({left: -window.innerWidth / 2, behavior: 'smooth'});
  }

  function scrollRight(event) {
    scrollerRef.current.scrollBy({left: window.innerWidth / 2, behavior: 'smooth'});
  }

  useEffect(() => {
    const scroller = scrollerRef.current;

    function updateCanScroll() {
      setCanScrollLeft(scroller.scrollLeft > 0);
      setCanScrollRight(
        scroller.scrollLeft < scroller.scrollWidth - scroller.clientWidth
      );
    }

    updateCanScroll();
    scroller.addEventListener('scroll', updateCanScroll);
    return () => scroller.removeEventListener('scroll', updateCanScroll);
  });

  useContentElementLifecycle({
    onActivate() {
      if (!scrolledRef.current) {
        scrolledRef.current = true;
        scrollRight();
      }
    }
  });

  useEffect(() => {
    if (selectPlans) {
      window.parent.postMessage({
        type: 'PRICING_TABLE_INFO',
        payload: {
          lines: lines.map(line => ({
            title: line.title,
            description: line.description,
            values: planTableaux.levels.reduce((result, level) => {
              result[level.name] = value(level, line, t);
              return result;
            }, {})
          }))
        }
      }, window.location.origin);
    }
  }, [selectPlans, planTableaux, lines, t]);

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.scrollButtons}>
          <button className={classNames(styles.scrollLeft,
                                        {[styles.inactiveScrollButton]:
                                          !canScrollLeft})}
                  onClick={scrollLeft}>
            <img src={chevronLeft} alt="Left" />
          </button>
          <button className={classNames(styles.scrollRight,
                                        {[styles.inactiveScrollButton]:
                                          !canScrollRight})}
                  onClick={scrollRight}>
            <img src={chevronRight} alt="right" />
          </button>
        </div>
        <div className={styles.scroller} ref={scrollerRef}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.lineLabel}></th>
                {planTableaux.levels.map((level) =>
                  <th key={level.name}
                      className={classNames(styles.level, styles.levelHead)}>
                    {level.name}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {lines.map(line =>
                <Row key={line.id}
                     levels={planTableaux.levels}
                     line={line}
                     t={t} />
              )}
            </tbody>
          </table>
        </div>
      </div>

      {configuration.displayEnterpriseBox &&
       <div className={styles.enterprise}>
         <h3 className={styles.enterpriseHeader}>{t.enterpriseHeader}</h3>
         <p>{t.enterpriseText}</p>

         <a href={t.newTicketUrl}
            target="_blank"
            rel="noreferrer noopener"
            className={styles.button}
            data-event="Enterprise Contact Button: Click">
           {t.enterpriseButton}
         </a>
       </div>}

      <div className={styles.finePrint}>
        {t.finePrint}
      </div>
    </>
  );
}

function Row({line, levels, t}) {
  return (
    <tr className={classNames(
      styles.line,
      {[styles.spaceBefore]: linesWithSpaceBefore.includes(line.levelProperty)}
    )}>
      <td className={classNames(styles.lineLabel,
                                {[styles.withInfoBox]:
                                  !utils.isBlank(line.description)})}>
        <span className={styles.lineLabelText}>
          {line.title}

          <div className={styles.infoBox}>
            {line.description}
          </div>
        </span>
      </td>
      {levels.map((level) =>
        <td key={level.name} className={styles.level}>
          {renderValue(level, line, t)}
        </td>
      )}
    </tr>
  );
}

function renderValue(level, line, t) {
  const result = value(level, line, t);

  if (result === true) {
    return <img alt={t.yes} src={checkmark} />;
  }
  else if (result === false) {
    return <span className={styles.unchecked}>-</span>
  }

  return result;
}

function value(level, line, t) {
  const variant = level.variants.monthly.eur;

  let levelProperty = line.levelProperty;

  if (levelProperty === 'hasPlausibleAnalytics') {
    levelProperty = 'hasGoogleAnalytics';
  }
  else if (levelProperty === 'hasCustomConsent') {
    levelProperty = 'hasCustomAnalytics';
  }

  let value = levelProperty ? variant[levelProperty] : '';

  // Can be replaced once we have all required info in the
  // plan variant objects
  if (line.levelProperty === 'hasTicketSupport' ||
      line.levelProperty === 'pageflowClassic' ||
      line.levelProperty === 'pageflowNext') {
    value = true;
  }
  else if (line.levelProperty === 'sites') {
    value = level.name === 'premium' ? 3 : 1;
  }
  else if ((line.levelProperty === 'hasExtendedSupport' ||
            line.levelProperty === 'hasCustomCertificate' ||
            line.levelProperty === 'hasSso' ||
            line.levelProperty === 'additionalSites') &&
           level.name === 'premium') {
    value = true;
  }
  else if (line.levelProperty === 'cdnTraffic' ||
           line.levelProperty === 'mediaEncoding') {
    value = level.name === 'premium' ? 'unlimited' : 'fairUse';
  }

  if (value === 'fairUse') {
    return t.fairUse;
  }
  else if (value === -1 || value === 'unlimited') {
    return t.unlimited;
  }

  return value;
}
